import type { Language, VerifyData } from "@/lib/api/types/verify";
import { updateUserLocale } from "~/lib/api/users/updateUser";
import { VERIFY_QUERY_KEY } from "~/stores/useUserStore";
import {
    getBrowserLanguageCode,
    resolveChosenLanguage,
} from "~/utils/localeResolver";

type User = VerifyData["user"];

export function useLocaleUpdate() {
    const queryClient = useQueryClient();
    const localSettingsStore = useLocalSettingsStore();
    const { errorHandler } = useErrorHandler();

    async function updateLocale(userId: number, locale: Language) {
        try {
            await updateUserLocale(userId, locale.id);
            queryClient.setQueriesData<VerifyData>(
                {
                    predicate: (query) =>
                        query.queryKey[0] === VERIFY_QUERY_KEY,
                },
                (cached) =>
                    cached
                        ? {
                              ...cached,
                              user: { ...cached.user, user_locale: locale },
                          }
                        : cached,
            );
        } catch (error) {
            await errorHandler(error);
        }
    }

    /**
     * Syncs the locale store with the server,
     * falling back to browser language when no locale is set for the user.
     */
    async function syncInitialLocale(user: User) {
        if (user.user_locale) {
            localSettingsStore.locale = user.user_locale.code;

            return;
        }

        const browserCode = getBrowserLanguageCode();
        const chosenLanguage = resolveChosenLanguage(
            browserCode,
            user.app.languages,
            user.app.language_default,
        );
        await updateLocale(user.id, chosenLanguage);
        localSettingsStore.locale = chosenLanguage.code;
    }

    return { syncInitialLocale, updateLocale };
}
